import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/app/components/ui/sheet';
import { useWorkPackage } from '@/hooks/use-work-package';
import { OverrideMarker } from '@/components/crew/override-marker';
import type {
  WorkPackageCrewMember,
  WorkPackageGap,
  WorkPackageGearManifest,
  WorkPackageGearManifestAsset,
  WorkPackageGearRequest,
  WorkPackageJSA,
  WorkPackageRescueKitReadiness,
  WorkPackageRescuePlan,
  WorkPackageSWP,
} from '@/hooks/use-work-package';
import {
  AlertTriangle,
  Award,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  HardHat,
  Loader2,
  MapPin,
  Package,
  Phone,
  Printer,
  Shield,
  ShieldAlert,
  Truck,
  User,
  Users,
  Wrench,
  XOctagon,
} from 'lucide-react';
import { resolveMediaUrl } from '@/lib/api-client';
import { formatDateOnly } from '@/lib/date-utils';

// ─── Status badge ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    approved: 'bg-green-100 text-green-700',
    valid: 'bg-green-100 text-green-700',
    pending_signatures: 'bg-yellow-100 text-yellow-700',
    pending_review: 'bg-yellow-100 text-yellow-700',
    draft: 'bg-gray-100 text-gray-600',
    expired: 'bg-red-100 text-red-700',
    expiring_soon: 'bg-amber-100 text-amber-700',
    requested: 'bg-blue-100 text-blue-700',
    fulfilled: 'bg-indigo-100 text-indigo-700',
    in_transit: 'bg-purple-100 text-purple-700',
    received: 'bg-green-100 text-green-700',
  };
  const display = status.replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${styles[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {display}
    </span>
  );
}

// ─── Gap banner ──────────────────────────────────────────────────────────────

function GapBanner({ gaps }: { gaps: WorkPackageGap[] }) {
  if (gaps.length === 0) return null;
  const blockers = gaps.filter((g) => g.severity === 'blocker');
  const warnings = gaps.filter((g) => g.severity === 'warning');
  return (
    <div className="space-y-1.5" data-testid="work-package-gaps">
      {blockers.map((g) => (
        <div key={g.key} className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          <XOctagon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{g.label}</span>
        </div>
      ))}
      {warnings.map((g) => (
        <div key={g.key} className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{g.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionHeader({ icon, label, count }: { icon: React.ReactNode; label: string; count?: number }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-1.5">
      {icon}
      {label}
      {count !== undefined && <span className="text-gray-400 font-normal">({count})</span>}
    </div>
  );
}

// ─── Crew section ────────────────────────────────────────────────────────────

function CrewSection({ crew }: { crew: WorkPackageCrewMember[] }) {
  if (crew.length === 0) {
    return (
      <>
        <SectionHeader icon={<Users className="h-3 w-3" />} label="Crew" count={0} />
        <p className="text-xs text-gray-400 italic">No crew assigned.</p>
      </>
    );
  }
  return (
    <>
      <SectionHeader icon={<Users className="h-3 w-3" />} label="Crew" count={crew.length} />
      <div className="space-y-2">
        {crew.map((m) => (
          <div
            key={m.assignment_id}
            className="rounded-md border border-gray-200 px-3 py-2"
          >
            <div className="flex items-center justify-between">
              <a
                href={m.url}
                className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
              >
                {m.name}
              </a>
              {m.role && <span className="text-[10px] text-gray-500">{m.role}</span>}
            </div>
            {m.override_reason && (
              <OverrideMarker reason={m.override_reason} className="mt-1" />
            )}
            {m.certifications.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {m.certifications.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-0.5">
                    <Award className="h-3 w-3 text-gray-400" />
                    <StatusBadge status={c.status} />
                    <span className="text-[10px] text-gray-600">{c.cert_name}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Safety section ──────────────────────────────────────────────────────────

function SafetyDocsSection({
  jsas,
  swps,
  rescuePlans,
}: {
  jsas: WorkPackageJSA[];
  swps: WorkPackageSWP[];
  rescuePlans: WorkPackageRescuePlan[];
}) {
  const hasAnything = jsas.length > 0 || swps.length > 0 || rescuePlans.length > 0;
  return (
    <>
      <SectionHeader icon={<Shield className="h-3 w-3" />} label="Safety Documents" />

      {!hasAnything && <p className="text-xs text-gray-400 italic">No safety documents on file.</p>}

      {jsas.length > 0 && (
        <div className="space-y-1 mb-2">
          <div className="text-[10px] font-medium text-gray-500">JSAs</div>
          {jsas.map((j) => (
            <a
              key={j.id}
              href={j.url}
              className="flex items-center justify-between rounded border border-gray-100 px-2 py-1.5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <ShieldAlert className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-700 truncate">{j.title}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] text-gray-400">{formatDateOnly(j.date)}</span>
                <StatusBadge status={j.status} />
              </div>
            </a>
          ))}
        </div>
      )}

      {swps.length > 0 && (
        <div className="space-y-1 mb-2">
          <div className="text-[10px] font-medium text-gray-500">Safe Work Plans</div>
          {swps.map((s) => (
            <a
              key={s.id}
              href={s.url}
              className="flex items-center justify-between rounded border border-gray-100 px-2 py-1.5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <FileText className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-700 truncate">{s.title}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] text-gray-400">{formatDateOnly(s.date)}</span>
                <StatusBadge status={s.status} />
              </div>
            </a>
          ))}
        </div>
      )}

      {rescuePlans.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-medium text-gray-500">Rescue Plans</div>
          {rescuePlans.map((rp) => (
            <div key={rp.id} className="rounded border border-gray-100 px-2 py-1.5">
              <a href={rp.url} className="flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-1.5 min-w-0">
                  <HardHat className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-700 truncate">{rp.title}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] text-gray-400">{formatDateOnly(rp.date)}</span>
                  <StatusBadge status={rp.status} />
                </div>
              </a>
              {rp.nearest_hospital && (
                <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Hospital: {rp.nearest_hospital}
                </div>
              )}
              {rp.rescue_equipment.length > 0 && (
                <div className="text-[10px] text-gray-500 mt-0.5">
                  Rescue gear: {rp.rescue_equipment.map((e) => `${e.equipment_name} x${e.quantity}`).join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Gear manifest section ───────────────────────────────────────────────────

const MANIFEST_STATE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  requested: { label: 'Requested', color: 'text-blue-600', icon: <Package className="h-3 w-3" /> },
  staged: { label: 'Staged', color: 'text-indigo-600', icon: <Package className="h-3 w-3" /> },
  in_transit: { label: 'In Transit', color: 'text-purple-600', icon: <Truck className="h-3 w-3" /> },
  received: { label: 'Received', color: 'text-green-600', icon: <CheckCircle2 className="h-3 w-3" /> },
  deployed: { label: 'Deployed', color: 'text-gray-700', icon: <Wrench className="h-3 w-3" /> },
  inspection_due: { label: 'Inspection Due', color: 'text-amber-600', icon: <AlertTriangle className="h-3 w-3" /> },
};

function GearManifestAssetRow({
  item,
  inspectionDue = false,
}: {
  item: WorkPackageGearManifestAsset;
  inspectionDue?: boolean;
}) {
  const className = `flex items-center justify-between rounded border border-gray-100 px-2 py-1 ${
    item.url ? `${inspectionDue ? 'hover:bg-amber-50' : 'hover:bg-gray-50'} transition-colors` : ''
  }`;
  const content = (
    <>
      <span className={`text-xs truncate ${inspectionDue ? 'text-amber-700' : 'text-gray-700'}`}>
        {item.asset_name}
      </span>
      <span className={`text-[10px] ${inspectionDue ? 'text-amber-500' : 'text-gray-400'}`}>
        {inspectionDue ? item.next_inspection_due : item.serial_number}
      </span>
    </>
  );

  return item.url ? (
    <a href={item.url} className={className}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

function GearManifestSection({
  projectId,
  gearManifest,
  gearRequests,
  gearDeployedCount,
}: {
  projectId: number | null;
  gearManifest: WorkPackageGearManifest;
  gearRequests: WorkPackageGearRequest[];
  gearDeployedCount: number;
}) {
  const hasAny =
    gearManifest.requested.length > 0 ||
    gearManifest.staged.length > 0 ||
    gearManifest.in_transit.length > 0 ||
    gearManifest.received.length > 0 ||
    gearManifest.deployed.length > 0 ||
    gearRequests.length > 0 ||
    gearDeployedCount > 0;

  return (
    <>
      <SectionHeader icon={<Package className="h-3 w-3" />} label="Gear Manifest" />
      {!hasAny && (
        <p className="text-xs text-gray-400 italic">No gear requests or deployments.</p>
      )}
      {hasAny && (
        <div className="space-y-2" data-testid="gear-manifest">
          {/* State-based rows */}
          {(['requested', 'staged', 'in_transit', 'received'] as const).map((state) => {
            const items = gearManifest[state];
            if (items.length === 0) return null;
            const meta = MANIFEST_STATE_META[state];
            return (
              <div key={state}>
                <div className={`flex items-center gap-1 text-[10px] font-medium ${meta.color} mb-0.5`}>
                  {meta.icon}
                  {meta.label} ({items.length})
                </div>
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      className="flex items-center justify-between rounded border border-gray-100 px-2 py-1 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-xs text-gray-700 truncate">
                        {item.label}
                        {item.needed_by && <span className="text-gray-400 ml-1">· Due {item.needed_by}</span>}
                      </span>
                      {item.priority !== 'normal' && (
                        <span className="text-[10px] text-amber-600 font-medium capitalize">{item.priority}</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Deployed assets */}
          {gearManifest.deployed.length > 0 && (
            <div>
              <div className={`flex items-center gap-1 text-[10px] font-medium ${MANIFEST_STATE_META.deployed.color} mb-0.5`}>
                {MANIFEST_STATE_META.deployed.icon}
                Deployed ({gearManifest.deployed.length})
              </div>
              <div className="space-y-0.5">
                {gearManifest.deployed.slice(0, 10).map((item) => (
                  <GearManifestAssetRow key={item.deployment_id} item={item} />
                ))}
                {gearManifest.deployed.length > 10 && (
                  <span className="text-[10px] text-gray-400 italic">+{gearManifest.deployed.length - 10} more</span>
                )}
              </div>
            </div>
          )}

          {/* Inspection due */}
          {gearManifest.inspection_due.length > 0 && (
            <div>
              <div className={`flex items-center gap-1 text-[10px] font-medium ${MANIFEST_STATE_META.inspection_due.color} mb-0.5`}>
                {MANIFEST_STATE_META.inspection_due.icon}
                Inspection Due ({gearManifest.inspection_due.length})
              </div>
              <div className="space-y-0.5">
                {gearManifest.inspection_due.map((item) => (
                  <GearManifestAssetRow
                    key={item.deployment_id}
                    item={item}
                    inspectionDue
                  />
                ))}
              </div>
            </div>
          )}

          {/* Remediation links */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            <a href="/gear-requests" className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 transition-colors">
              <Package className="h-3 w-3" /> Request Gear
            </a>
            {projectId != null && (
              <a href={`/deployments/send-gear?project=${projectId}`} className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 transition-colors" data-testid="work-package-send-gear">
                <Package className="h-3 w-3" /> Send Gear
              </a>
            )}
            <a href="/inspections/batch" className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 transition-colors">
              <Wrench className="h-3 w-3" /> Inspect Gear
            </a>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Rescue-kit readiness section ────────────────────────────────────────────

const KIT_STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  ready: { bg: 'bg-green-50', text: 'text-green-700', label: 'Ready' },
  inspection_due: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Inspection Due' },
  no_data: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'No Equipment' },
};

function RescueKitReadinessSection({ readiness }: { readiness: WorkPackageRescueKitReadiness[] }) {
  if (readiness.length === 0) return null;
  return (
    <>
      <SectionHeader icon={<HardHat className="h-3 w-3" />} label="Rescue-Kit Readiness" count={readiness.length} />
      <div className="space-y-2" data-testid="rescue-kit-readiness">
        {readiness.map((rk) => {
          const style = KIT_STATUS_STYLES[rk.kit_status] ?? KIT_STATUS_STYLES.no_data;
          return (
            <div key={rk.rescue_plan_id} className={`rounded-md border border-gray-200 px-3 py-2 ${style.bg}`}>
              <div className="flex items-center justify-between mb-1">
                <a href={rk.rescue_plan_url} className="text-xs font-medium text-gray-900 truncate hover:underline">
                  {rk.rescue_plan_title}
                </a>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${style.text} ${style.bg}`}>
                  {rk.kit_status === 'ready' && <CheckCircle2 className="h-3 w-3 mr-0.5" />}
                  {rk.kit_status === 'inspection_due' && <AlertTriangle className="h-3 w-3 mr-0.5" />}
                  {style.label}
                </span>
              </div>
              {rk.equipment.length > 0 && (
                <div className="space-y-0.5">
                  {rk.equipment.map((eq) => (
                    <div
                      key={eq.id}
                      className={`flex items-center justify-between text-[10px] ${eq.inspection_overdue ? 'text-amber-700' : 'text-gray-600'}`}
                    >
                      <span className="truncate">
                        {eq.equipment_name} x{eq.quantity}
                        {eq.location_staged && <span className="text-gray-400 ml-1">@ {eq.location_staged}</span>}
                      </span>
                      {eq.inspection_overdue && eq.next_inspection_due && (
                        <span className="text-amber-600 flex-shrink-0 ml-1">Insp. due {eq.next_inspection_due}</span>
                      )}
                      {eq.url && (
                        <a href={eq.url} className="text-blue-500 hover:text-blue-700 flex-shrink-0 ml-1">
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {rk.equipment.length === 0 && (
                <p className="text-[10px] text-gray-400 italic">No equipment listed — update rescue plan.</p>
              )}
              {/* Remediation link */}
              <div className="mt-1">
                <a
                  href={rk.rescue_plan_url}
                  className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-2.5 w-2.5" /> Update Rescue Plan
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

interface WorkPackagePanelProps {
  open: boolean;
  onClose: () => void;
  projectId: number | null;
}

export function WorkPackagePanel({ open, onClose, projectId }: WorkPackagePanelProps) {
  const { data, isLoading, error } = useWorkPackage(projectId, open);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle data-testid="work-package-title">
            {data ? `${data.project.name} — Work Package` : 'Work Package'}
          </SheetTitle>
          <SheetDescription>
            Operational packet: crew, safety, gear, and compliance for this project.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-4 pb-6 space-y-1">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading work package...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load work package.
            </div>
          )}

          {data && (
            <>
              {/* Project header */}
              <div className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-900">{data.project.name}</span>
                    <span className="ml-2 text-xs text-gray-500">{data.project.project_number}</span>
                  </div>
                  <StatusBadge status={data.project.status} />
                </div>
                {data.project.department && (
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    Dept: {data.project.department}
                  </div>
                )}
                {(data.project.start_date || data.project.end_date) && (
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {data.project.start_date &&
                      `Start: ${formatDateOnly(data.project.start_date)}`}
                    {data.project.end_date &&
                      ` — End: ${formatDateOnly(data.project.end_date)}`}
                  </div>
                )}
              </div>

              {/* Gaps / actionable warnings */}
              <GapBanner gaps={data.gaps} />

              {/* Client / site */}
              <SectionHeader icon={<Building2 className="h-3 w-3" />} label="Client & Site" />
              <div className="rounded-md border border-gray-100 px-3 py-2 text-xs space-y-1">
                {data.client ? (
                  <>
                    <a className="font-medium text-gray-800 hover:underline" href={data.client.url}>
                      {data.client.name}
                    </a>
                    {data.client.contact_name && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <User className="h-3 w-3 text-gray-400" /> {data.client.contact_name}
                      </div>
                    )}
                    {data.client.contact_phone && (
                      <a className="flex items-center gap-1 text-gray-600 hover:underline" href={`tel:${data.client.contact_phone}`}>
                        <Phone className="h-3 w-3 text-gray-400" /> {data.client.contact_phone}
                      </a>
                    )}
                    {data.client.contact_email && (
                      <a className="flex items-center gap-1 text-gray-600 hover:underline" href={`mailto:${data.client.contact_email}`}>
                        <ExternalLink className="h-3 w-3 text-gray-400" /> {data.client.contact_email}
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400 italic">No client assigned</span>
                )}
                {data.site.location_fk_name && (
                  data.site.url ? (
                    <a
                      href={data.site.url}
                      className="flex items-center gap-1 text-gray-600 pt-1 border-t border-gray-50 hover:underline"
                    >
                      <MapPin className="h-3 w-3 text-gray-400" />
                      {[data.site.location_fk_name, data.site.address, data.site.city, data.site.state_province]
                        .filter(Boolean)
                        .join(', ')}
                    </a>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-600 pt-1 border-t border-gray-50">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      {[data.site.location_fk_name, data.site.address, data.site.city, data.site.state_province]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )
                )}
              </div>

              {/* Crew */}
              <CrewSection crew={data.crew} />

              {/* Safety documents */}
              <SafetyDocsSection jsas={data.jsas} swps={data.safe_work_plans} rescuePlans={data.rescue_plans} />

              {/* Gear manifest */}
              <GearManifestSection
                projectId={projectId}
                gearManifest={data.gear_manifest}
                gearRequests={data.gear_requests}
                gearDeployedCount={data.gear_deployed.length}
              />

              {/* Rescue-kit readiness */}
              <RescueKitReadinessSection readiness={data.rescue_kit_readiness} />

              {/* Emergency contacts */}
              {data.emergency_contacts.length > 0 && (
                <>
                  <SectionHeader icon={<Phone className="h-3 w-3" />} label="Emergency Contacts" count={data.emergency_contacts.length} />
                  <div className="space-y-1">
                    {data.emergency_contacts.map((ec, i) => (
                      <div key={i} className="flex items-center justify-between rounded border border-gray-100 px-2 py-1.5 text-xs">
                        <span className="text-gray-700">{ec.contact_name} ({ec.relationship})</span>
                        <a className="text-gray-500 hover:underline" href={`tel:${ec.phone}`}>{ec.phone}</a>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Compliance links */}
              {data.compliance_links.length > 0 && (
                <>
                  <SectionHeader icon={<Shield className="h-3 w-3" />} label="Compliance Evidence" count={data.compliance_links.length} />
                  <div className="space-y-1">
                    {data.compliance_links.map((cl) => (
                      <div key={cl.id} className="flex items-center justify-between rounded border border-gray-100 px-2 py-1.5 text-xs">
                        <span className="text-gray-700 truncate">{cl.title}</span>
                        {cl.external_url && (
                          <a href={cl.external_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Documents */}
              {data.documents.length > 0 && (
                <>
                  <SectionHeader icon={<FileText className="h-3 w-3" />} label="Project Documents" count={data.documents.length} />
                  <div className="space-y-1">
                    {data.documents.map((doc) => {
                      const content = (
                        <>
                          <FileText className="h-3 w-3 text-gray-400" />
                          <span className="truncate">{doc.name}</span>
                          <StatusBadge status={doc.document_type} />
                        </>
                      );
                      const className = 'flex items-center gap-1.5 rounded border border-gray-100 px-2 py-1.5 text-xs text-gray-700';
                      return doc.url ? (
                        <a
                          key={doc.id}
                          href={resolveMediaUrl(doc.url)}
                          className={`${className} hover:bg-gray-50 transition-colors`}
                        >
                          {content}
                        </a>
                      ) : (
                        <div key={doc.id} className={className}>
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Print affordance */}
              <div className="pt-3 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => window.print()}
                  data-testid="work-package-print"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print / Share
                </button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
